import React from 'react'
import Badge from '../../common/badge/Badge'
import { BadgeColor } from '../../common/badge/badgeTypes'
import type {
    KitConvoCompanyMentionDto,
    KitConvoCurrentJobSearchParamsDto,
    KitConvoDebugScoringDto,
    KitConvoJobOfferedDto,
    KitConvoJobSearchExecutionDto,
    KitConvoLocationMentionDto,
    KitConvoPersonMentionDto,
    KitConvoTranscriptRawDataDto,
    KitConvoTranscriptTurnDto,
    KitConvoTargetSalaryMentionDto,
    KitConvoTargetJobTitleMentionDto,
    KitConvoToolCallDto,
} from '@/api/kit/KitApi'
import ProfilePic from '../../common/person/ProfilePic'
import LinkedInLink from '../../../components/LinkedInLink'
import { formatPersonName } from '@/utils/personUtils'
import { Star } from 'lucide-react'
import KitConversationTimeline from './KitConversationTimeline'
import ScoringResultsSection from './ScoringResultsSection'

type Props = {
    activeConvoId: string | null
    toolCalls: KitConvoToolCallDto[]
    transcriptRawData: KitConvoTranscriptRawDataDto | null
    transcriptTurns: KitConvoTranscriptTurnDto[]
    personMentions: KitConvoPersonMentionDto[]
    companyMentions: KitConvoCompanyMentionDto[]
    locationMentions: KitConvoLocationMentionDto[]
    targetJobTitleMentions: KitConvoTargetJobTitleMentionDto[]
    targetSalaryMentions: KitConvoTargetSalaryMentionDto[]
    currentJobSearchParams: KitConvoCurrentJobSearchParamsDto | null
    jobsOffered: KitConvoJobOfferedDto[]
    jobSearchExecutions: KitConvoJobSearchExecutionDto[]
    scoring: KitConvoDebugScoringDto | null
    kitConvoId: string | null
    onRetriggerComplete: () => void
    toolCallsError: string | null
    flashingToolCallIds?: Record<string, boolean>
}

const formatTimestamp = (value: string | null): string => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return value
    }
    return date.toLocaleString()
}

const formatPayload = (value: string | null): string => {
    if (!value) return '-'
    const trimmed = value.trim()
    if (!trimmed) return '-'
    try {
        const parsed = JSON.parse(trimmed)
        return JSON.stringify(parsed, null, 2)
    } catch {
        return value
    }
}

const getStatusBadgeColor = (statusCode: number | null): BadgeColor => {
    if (statusCode === null) {
        return BadgeColor.Neutral
    }
    if (statusCode >= 200 && statusCode < 300) {
        return BadgeColor.Success
    }
    return BadgeColor.Error
}

const getDurationBadgeColor = (durationMs: number): BadgeColor => {
    if (durationMs > 2000) {
        return BadgeColor.Error
    }
    if (durationMs > 1000) {
        return BadgeColor.Warning
    }
    return BadgeColor.Success
}

const formatDurationMs = (durationMs: number): string => {
    if (durationMs < 1000) {
        return `${Math.max(0, Math.round(durationMs))}ms`
    }
    const seconds = durationMs / 1000
    const precision = seconds < 10 ? 1 : 0
    return `${seconds.toFixed(precision)}s`
}

const getOfferedStatusBadgeColor = (
    status: KitConvoJobOfferedDto['status'],
): BadgeColor => {
    if (status === 'Interested') {
        return BadgeColor.Success
    }

    if (status === 'Not Interested') {
        return BadgeColor.Error
    }

    return BadgeColor.Neutral
}

const formatLocationLabel = (
    city: string | null | undefined,
    country: string | null | undefined,
): string => {
    const cityValue = city?.trim() ?? ''
    const countryValue = country?.trim() ?? ''

    if (cityValue && countryValue) {
        return `${cityValue}, ${countryValue}`
    }
    if (cityValue) {
        return cityValue
    }
    if (countryValue) {
        return countryValue
    }

    return 'Location unavailable'
}

const formatSalaryLabel = (
    currency: string,
    minSalary: number | null | undefined,
    maxSalary: number | null | undefined,
): string => {
    const minValue =
        typeof minSalary === 'number' && Number.isFinite(minSalary)
            ? Math.round(minSalary)
            : null
    const maxValue =
        typeof maxSalary === 'number' && Number.isFinite(maxSalary)
            ? Math.round(maxSalary)
            : null

    if (minValue !== null && maxValue !== null) {
        return `${currency}: ${minValue.toLocaleString()} - ${maxValue.toLocaleString()}`
    }
    if (minValue !== null) {
        return `${currency}: ${minValue.toLocaleString()}+`
    }
    if (maxValue !== null) {
        return `${currency}: up to ${maxValue.toLocaleString()}`
    }

    return `${currency}: -`
}

const formatSalarySetLabel = (
    salary: {
        minSalaryGBP: number | null | undefined
        maxSalaryGBP: number | null | undefined
        minSalaryUSD: number | null | undefined
        maxSalaryUSD: number | null | undefined
        minSalaryEUR: number | null | undefined
        maxSalaryEUR: number | null | undefined
    },
    inputCurrency: string,
): string => {
    const labels = [
        formatSalaryLabel('GBP', salary.minSalaryGBP, salary.maxSalaryGBP),
        formatSalaryLabel('USD', salary.minSalaryUSD, salary.maxSalaryUSD),
        formatSalaryLabel('EUR', salary.minSalaryEUR, salary.maxSalaryEUR),
    ]

    return `Input ${inputCurrency} | ${labels.join(' | ')}`
}

const MAX_RATING = 5

const normalizeRating = (value: number | null | undefined): number | null => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return null
    }
    const rounded = Math.round(value)
    return Math.min(MAX_RATING, Math.max(1, rounded))
}

const renderRatingStars = (rating: number | null): React.JSX.Element[] => {
    const filledCount = rating ?? 0
    return Array.from({ length: MAX_RATING }, (_value, index) => {
        const isFilled = index < filledCount
        return (
            <Star
                key={`rating-star-${index}`}
                className={
                    isFilled
                        ? 'h-4 w-4 text-amber-500'
                        : 'h-4 w-4 text-muted-foreground/40'
                }
                strokeWidth={1.6}
                fill={isFilled ? 'currentColor' : 'none'}
            />
        )
    })
}

export default function KitDebugConsole({
    activeConvoId,
    toolCalls,
    transcriptRawData,
    transcriptTurns,
    personMentions,
    companyMentions,
    locationMentions,
    targetJobTitleMentions,
    targetSalaryMentions,
    currentJobSearchParams,
    jobsOffered,
    jobSearchExecutions,
    scoring,
    kitConvoId,
    onRetriggerComplete,
    toolCallsError,
    flashingToolCallIds,
}: Props): React.JSX.Element {
    const flashingIds = flashingToolCallIds ?? {}

    return (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-foreground">
                        Debug Console
                    </h2>
                    <p className="text-xs text-muted-foreground">
                        Tool calls for the current Kit conversation.
                    </p>
                </div>
                <div className="text-xs text-muted-foreground break-all">
                    {activeConvoId
                        ? `convId: ${activeConvoId}`
                        : 'No active conversation'}
                </div>
            </div>

            <ScoringResultsSection
                scoring={scoring}
                kitConvoId={kitConvoId}
                onRetriggerComplete={onRetriggerComplete}
            />

            <div className="mt-4 rounded-2xl border border-border bg-white p-4 shadow-[var(--shadow-soft)]">
                <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-foreground">
                        Tool Calls
                    </h3>
                    {toolCallsError ? (
                        <span className="text-xs text-destructive">
                            {toolCallsError}
                        </span>
                    ) : null}
                </div>

                <div className="mt-3 overflow-auto">
                    <table className="min-w-full text-xs">
                        <thead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            <tr className="border-b border-border">
                                <th className="pb-2 pr-3 text-left font-semibold">
                                    Time
                                </th>
                                <th className="pb-2 pr-3 text-left font-semibold">
                                    Tool
                                </th>
                                <th className="pb-2 pr-3 text-left font-semibold">
                                    URL
                                </th>
                                <th className="pb-2 pr-3 text-left font-semibold">
                                    Status
                                </th>
                                <th className="pb-2 text-left font-semibold">
                                    Details
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {toolCalls.length === 0 ? (
                                <tr>
                                    <td
                                        className="py-4 text-center text-xs text-muted-foreground"
                                        colSpan={5}
                                    >
                                        {activeConvoId
                                            ? 'No tool calls yet.'
                                            : 'Start a Kit conversation to see tool calls.'}
                                    </td>
                                </tr>
                            ) : (
                                toolCalls.map((call) => {
                                    const isFlashing = Boolean(
                                        flashingIds[call.id],
                                    )
                                    const statusLabel =
                                        call.responseStatusCode === null
                                            ? 'PENDING'
                                            : String(call.responseStatusCode)
                                    return (
                                        <tr
                                            key={call.id}
                                            className={`border-b border-border/70 transition-colors ${
                                                isFlashing
                                                    ? 'bg-yellow-100'
                                                    : 'bg-transparent'
                                            }`}
                                        >
                                            <td className="py-2 pr-3 align-top whitespace-nowrap text-muted-foreground">
                                                <div>
                                                    {formatTimestamp(
                                                        call.calledAt,
                                                    )}
                                                </div>
                                                <div className="text-[10px] text-muted-foreground/70">
                                                    {call.respondedAt
                                                        ? `Resp ${formatTimestamp(
                                                              call.respondedAt,
                                                          )}`
                                                        : 'Awaiting response'}
                                                </div>
                                                <div className="mt-1">
                                                    {call.responseDurationMs !=
                                                    null ? (
                                                        <Badge
                                                            color={getDurationBadgeColor(
                                                                call.responseDurationMs,
                                                            )}
                                                        >
                                                            {formatDurationMs(
                                                                call.responseDurationMs,
                                                            )}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-[10px] text-muted-foreground/70">
                                                            -
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-2 pr-3 align-top">
                                                <div className="text-xs font-semibold text-foreground">
                                                    {call.toolName}
                                                </div>
                                                <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                                                    {call.httpMethod}
                                                </div>
                                            </td>
                                            <td className="py-2 pr-3 align-top">
                                                <div className="text-xs text-foreground break-all">
                                                    {call.urlCalled}
                                                </div>
                                            </td>
                                            <td className="py-2 pr-3 align-top">
                                                <Badge
                                                    color={getStatusBadgeColor(
                                                        call.responseStatusCode,
                                                    )}
                                                >
                                                    {statusLabel}
                                                </Badge>
                                            </td>
                                            <td className="py-2 align-top">
                                                <details className="text-xs text-muted-foreground">
                                                    <summary className="cursor-pointer text-primary">
                                                        View
                                                    </summary>
                                                    <div className="mt-2 space-y-2">
                                                        <div>
                                                            <p className="text-[11px] font-semibold text-foreground">
                                                                Headers
                                                            </p>
                                                            <pre className="mt-1 max-h-40 overflow-auto rounded-lg bg-secondary/40 p-2 text-[10px] leading-relaxed text-foreground">
                                                                {formatPayload(
                                                                    call.headers,
                                                                )}
                                                            </pre>
                                                        </div>
                                                        <div>
                                                            <p className="text-[11px] font-semibold text-foreground">
                                                                Query Params
                                                            </p>
                                                            <pre className="mt-1 max-h-32 overflow-auto rounded-lg bg-secondary/40 p-2 text-[10px] leading-relaxed text-foreground">
                                                                {formatPayload(
                                                                    call.urlQueryParams,
                                                                )}
                                                            </pre>
                                                        </div>
                                                        <div>
                                                            <p className="text-[11px] font-semibold text-foreground">
                                                                Body
                                                            </p>
                                                            <pre className="mt-1 max-h-40 overflow-auto rounded-lg bg-secondary/40 p-2 text-[10px] leading-relaxed text-foreground">
                                                                {formatPayload(
                                                                    call.body,
                                                                )}
                                                            </pre>
                                                        </div>
                                                        <div>
                                                            <p className="text-[11px] font-semibold text-foreground">
                                                                Response
                                                            </p>
                                                            <pre className="mt-1 max-h-40 overflow-auto rounded-lg bg-secondary/40 p-2 text-[10px] leading-relaxed text-foreground">
                                                                {formatPayload(
                                                                    call.responseBody,
                                                                )}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                </details>
                                            </td>
                                        </tr>
                                    )
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="mt-6 border-t border-border pt-4">
                    <h3 className="text-sm font-semibold text-foreground">
                        People Mentioned
                    </h3>

                    <div className="mt-3 space-y-3">
                        {personMentions.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                {activeConvoId
                                    ? 'No mentions yet.'
                                    : 'Start a Kit conversation to see mentions.'}
                            </p>
                        ) : (
                            personMentions.map((mention) => {
                                const ratingValue = normalizeRating(
                                    mention.rating,
                                )
                                const ratingReasonText =
                                    mention.ratingReason?.trim() || ''

                                return (
                                    <div
                                        key={mention.toolCallId}
                                        className="rounded-xl border border-border/70 bg-secondary/30 p-3"
                                    >
                                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                                            <span>Mentioned</span>
                                            <span>
                                                {formatTimestamp(
                                                    mention.calledAt,
                                                )}
                                            </span>
                                        </div>
                                        <div className="mt-2 rounded-lg border border-border/60 bg-white/70 px-3 py-2">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex items-center gap-1">
                                                    {renderRatingStars(
                                                        ratingValue,
                                                    )}
                                                </div>
                                                <span className="text-[11px] text-muted-foreground">
                                                    {ratingValue
                                                        ? `${ratingValue}/5`
                                                        : 'No rating recorded yet.'}
                                                </span>
                                            </div>
                                            <div className="mt-1 text-[11px] text-muted-foreground">
                                                Rating reason:{' '}
                                                <span className="text-foreground">
                                                    {ratingReasonText || '-'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mt-2 space-y-2">
                                            {mention.persons.length === 0 ? (
                                                <p className="text-xs text-muted-foreground">
                                                    No potential matches.
                                                </p>
                                            ) : (
                                                mention.persons.map(
                                                    (person) => {
                                                        const headlineText =
                                                            person.headline?.trim() ||
                                                            'Headline not available'
                                                        const connectionSummaryText =
                                                            person.connectionSummary?.trim() ||
                                                            'No shared work history found'

                                                        return (
                                                            <div
                                                                key={person.id}
                                                                className="flex items-start gap-3 text-xs"
                                                            >
                                                                <ProfilePic
                                                                    src={
                                                                        person.linkedInProfilePicUrl ??
                                                                        undefined
                                                                    }
                                                                    size={32}
                                                                    alt={formatPersonName(
                                                                        person,
                                                                    )}
                                                                />
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <span className="font-semibold text-foreground">
                                                                            {formatPersonName(
                                                                                person,
                                                                            ) ||
                                                                                person.id}
                                                                        </span>
                                                                        {person.linkedInUrl ? (
                                                                            <LinkedInLink
                                                                                url={
                                                                                    person.linkedInUrl
                                                                                }
                                                                                ariaLabel="Open LinkedIn profile"
                                                                            />
                                                                        ) : null}
                                                                    </div>
                                                                    <p className="mt-1 text-[11px] text-muted-foreground">
                                                                        {
                                                                            headlineText
                                                                        }
                                                                    </p>
                                                                    <p className="mt-1 text-[11px] text-muted-foreground">
                                                                        {
                                                                            connectionSummaryText
                                                                        }
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )
                                                    },
                                                )
                                            )}
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>
                </div>

                <div className="mt-6 border-t border-border pt-4">
                    <h3 className="text-sm font-semibold text-foreground">
                        Used to work at companies
                    </h3>

                    <div className="mt-3 space-y-3">
                        {companyMentions.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                {activeConvoId
                                    ? 'No company mentions yet.'
                                    : 'Start a Kit conversation to see company mentions.'}
                            </p>
                        ) : (
                            companyMentions.map((mention) => {
                                const companyName =
                                    mention.companyName?.trim() ||
                                    mention.matchingName?.trim() ||
                                    'Company name unavailable'
                                const matchingName =
                                    mention.matchingName?.trim() || ''
                                const showMatchingName =
                                    Boolean(matchingName) &&
                                    matchingName.toLowerCase() !==
                                        companyName.toLowerCase()

                                return (
                                    <div
                                        key={`${mention.toolCallId}-${matchingName || companyName}`}
                                        className="rounded-xl border border-border/70 bg-secondary/30 p-3"
                                    >
                                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                                            <span>Used to work at</span>
                                            <span>
                                                {formatTimestamp(
                                                    mention.calledAt,
                                                )}
                                            </span>
                                        </div>
                                        <div className="mt-2">
                                            <p className="text-xs font-semibold text-foreground">
                                                {companyName}
                                            </p>
                                            {showMatchingName ? (
                                                <p className="text-[11px] text-muted-foreground">
                                                    Matching name:{' '}
                                                    <span className="text-foreground">
                                                        {matchingName}
                                                    </span>
                                                </p>
                                            ) : null}
                                        </div>
                                        <div className="mt-2">
                                            <p className="text-[11px] font-semibold text-foreground">
                                                Resolved companies
                                            </p>
                                            {mention.resolvedCompanies.length >
                                            0 ? (
                                                <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                                                    {mention.resolvedCompanies.map(
                                                        (company) => (
                                                            <li
                                                                key={company.id}
                                                                className="flex items-center justify-between gap-2"
                                                            >
                                                                <span>
                                                                    {
                                                                        company.name
                                                                    }
                                                                </span>
                                                                <span className="text-[10px] text-muted-foreground/70">
                                                                    {company.id}
                                                                </span>
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            ) : (
                                                <p className="mt-1 text-xs text-muted-foreground">
                                                    No matches yet.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>
                </div>

                <div className="mt-6 border-t border-border pt-4">
                    <h3 className="text-sm font-semibold text-foreground">
                        Target job locations
                    </h3>

                    <div className="mt-3 space-y-3">
                        {locationMentions.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                {activeConvoId
                                    ? 'No target location mentions yet.'
                                    : 'Start a Kit conversation to see location mentions.'}
                            </p>
                        ) : (
                            locationMentions.map((mention, index) => (
                                <div
                                    key={`${mention.toolCallId}-${index}`}
                                    className="rounded-xl border border-border/70 bg-secondary/30 p-3"
                                >
                                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                                        <span>Target location</span>
                                        <span>
                                            {formatTimestamp(mention.calledAt)}
                                        </span>
                                    </div>
                                    <p className="mt-2 text-xs font-semibold text-foreground">
                                        {formatLocationLabel(
                                            mention.city,
                                            mention.country,
                                        )}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="mt-6 border-t border-border pt-4">
                    <h3 className="text-sm font-semibold text-foreground">
                        Target job titles
                    </h3>

                    <div className="mt-3 space-y-3">
                        {targetJobTitleMentions.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                {activeConvoId
                                    ? 'No target job title mentions yet.'
                                    : 'Start a Kit conversation to see title mentions.'}
                            </p>
                        ) : (
                            targetJobTitleMentions.map((mention, index) => (
                                <div
                                    key={`${mention.toolCallId}-${mention.jobTitle}-${index}`}
                                    className="rounded-xl border border-border/70 bg-secondary/30 p-3"
                                >
                                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                                        <span>Target job title</span>
                                        <span>
                                            {formatTimestamp(mention.calledAt)}
                                        </span>
                                    </div>
                                    <p className="mt-2 text-xs font-semibold text-foreground">
                                        {mention.jobTitle}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="mt-6 border-t border-border pt-4">
                    <h3 className="text-sm font-semibold text-foreground">
                        Target salaries
                    </h3>

                    <div className="mt-3 space-y-3">
                        {targetSalaryMentions.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                {activeConvoId
                                    ? 'No target salary mentions yet.'
                                    : 'Start a Kit conversation to see salary mentions.'}
                            </p>
                        ) : (
                            targetSalaryMentions.map((mention, index) => (
                                <div
                                    key={`${mention.toolCallId}-${mention.inputCurrency}-${index}`}
                                    className="rounded-xl border border-border/70 bg-secondary/30 p-3"
                                >
                                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                                        <span>Target salary</span>
                                        <span>
                                            {formatTimestamp(mention.calledAt)}
                                        </span>
                                    </div>
                                    <p className="mt-2 text-xs font-semibold text-foreground">
                                        {formatSalarySetLabel(
                                            {
                                                minSalaryGBP:
                                                    mention.minSalaryGBP,
                                                maxSalaryGBP:
                                                    mention.maxSalaryGBP,
                                                minSalaryUSD:
                                                    mention.minSalaryUSD,
                                                maxSalaryUSD:
                                                    mention.maxSalaryUSD,
                                                minSalaryEUR:
                                                    mention.minSalaryEUR,
                                                maxSalaryEUR:
                                                    mention.maxSalaryEUR,
                                            },
                                            mention.inputCurrency,
                                        )}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="mt-6 border-t border-border pt-4">
                    <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-foreground">
                            Current Job Search Params
                        </h3>
                        <span className="text-[11px] text-muted-foreground">
                            {currentJobSearchParams
                                ? `nodeId: ${currentJobSearchParams.id}`
                                : 'Not created yet'}
                        </span>
                    </div>

                    {!activeConvoId ? (
                        <p className="mt-3 text-xs text-muted-foreground">
                            Start a Kit conversation to see current job search
                            params.
                        </p>
                    ) : !currentJobSearchParams ? (
                        <p className="mt-3 text-xs text-muted-foreground">
                            No current job search params node yet.
                        </p>
                    ) : (
                        <div className="mt-3 space-y-3">
                            <div className="rounded-xl border border-border/70 bg-secondary/30 p-3">
                                <p className="text-[11px] font-semibold text-foreground">
                                    sourceCompanies
                                </p>
                                {currentJobSearchParams.sourceCompanies
                                    .length === 0 ? (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        No connected source companies linked.
                                    </p>
                                ) : (
                                    <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                                        {currentJobSearchParams.sourceCompanies.map(
                                            (company) => (
                                                <li
                                                    key={company.id}
                                                    className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-white/80 p-2"
                                                >
                                                    <div className="min-w-0">
                                                        <p className="text-foreground">
                                                            {company.name}
                                                        </p>
                                                        <p className="text-[10px] text-muted-foreground/80">
                                                            dataProviderId:{' '}
                                                            {company.dataProviderId ??
                                                                '-'}
                                                        </p>
                                                    </div>
                                                    <span className="text-[10px] text-muted-foreground/70 break-all">
                                                        {company.id}
                                                    </span>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}
                            </div>

                            <div className="rounded-xl border border-border/70 bg-secondary/30 p-3">
                                <p className="text-[11px] font-semibold text-foreground">
                                    sourceCompanyMentions
                                </p>
                                {currentJobSearchParams.sourceCompanyMentions
                                    .length === 0 ? (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        No source company mentions linked.
                                    </p>
                                ) : (
                                    <ul className="mt-2 space-y-2 text-xs">
                                        {currentJobSearchParams.sourceCompanyMentions.map(
                                            (mention) => {
                                                const companyName =
                                                    mention.companyName?.trim() ||
                                                    mention.matchingName?.trim() ||
                                                    'Company name unavailable'
                                                const matchingName =
                                                    mention.matchingName?.trim() ||
                                                    ''
                                                const showMatchingName =
                                                    Boolean(matchingName) &&
                                                    matchingName.toLowerCase() !==
                                                        companyName.toLowerCase()

                                                return (
                                                    <li
                                                        key={mention.id}
                                                        className="rounded-lg border border-border/60 bg-white/80 p-2"
                                                    >
                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                            <span className="font-semibold text-foreground">
                                                                {companyName}
                                                            </span>
                                                            <span className="text-[10px] text-muted-foreground/70">
                                                                {mention.id}
                                                            </span>
                                                        </div>
                                                        {showMatchingName ? (
                                                            <p className="mt-1 text-[11px] text-muted-foreground">
                                                                Matching name:{' '}
                                                                <span className="text-foreground">
                                                                    {
                                                                        matchingName
                                                                    }
                                                                </span>
                                                            </p>
                                                        ) : null}
                                                        <div className="mt-1">
                                                            <p className="text-[11px] text-muted-foreground">
                                                                Resolved
                                                                companies
                                                            </p>
                                                            {mention
                                                                .resolvedCompanies
                                                                .length > 0 ? (
                                                                <ul className="mt-1 space-y-1 text-[11px] text-muted-foreground">
                                                                    {mention.resolvedCompanies.map(
                                                                        (
                                                                            company,
                                                                        ) => (
                                                                            <li
                                                                                key={
                                                                                    company.id
                                                                                }
                                                                                className="flex items-center justify-between gap-2"
                                                                            >
                                                                                <span>
                                                                                    {
                                                                                        company.name
                                                                                    }
                                                                                </span>
                                                                                <span className="text-[10px] text-muted-foreground/70">
                                                                                    {
                                                                                        company.id
                                                                                    }
                                                                                </span>
                                                                            </li>
                                                                        ),
                                                                    )}
                                                                </ul>
                                                            ) : (
                                                                <p className="text-[11px] text-muted-foreground">
                                                                    No matches
                                                                    yet.
                                                                </p>
                                                            )}
                                                        </div>
                                                    </li>
                                                )
                                            },
                                        )}
                                    </ul>
                                )}
                            </div>

                            <div className="rounded-xl border border-border/70 bg-secondary/30 p-3">
                                <p className="text-[11px] font-semibold text-foreground">
                                    sourceTargetJobTitles
                                </p>
                                {currentJobSearchParams.targetJobTitles
                                    .length === 0 ? (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        No target job titles yet.
                                    </p>
                                ) : (
                                    <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-foreground">
                                        {currentJobSearchParams.targetJobTitles.map(
                                            (jobTitle) => (
                                                <li key={jobTitle}>
                                                    {jobTitle}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}
                            </div>

                            <div className="rounded-xl border border-border/70 bg-secondary/30 p-3">
                                <p className="text-[11px] font-semibold text-foreground">
                                    sourceTargetSalaries
                                </p>
                                {currentJobSearchParams.targetSalaries
                                    .length === 0 ? (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        No target salary mentions yet.
                                    </p>
                                ) : (
                                    <ul className="mt-2 space-y-1 text-xs text-foreground">
                                        {currentJobSearchParams.targetSalaries.map(
                                            (targetSalary, index) => (
                                                <li
                                                    key={`${targetSalary.inputCurrency}-${index}`}
                                                    className="rounded-lg border border-border/60 bg-white/80 p-2"
                                                >
                                                    {formatSalarySetLabel(
                                                        {
                                                            minSalaryGBP:
                                                                targetSalary.minSalaryGBP,
                                                            maxSalaryGBP:
                                                                targetSalary.maxSalaryGBP,
                                                            minSalaryUSD:
                                                                targetSalary.minSalaryUSD,
                                                            maxSalaryUSD:
                                                                targetSalary.maxSalaryUSD,
                                                            minSalaryEUR:
                                                                targetSalary.minSalaryEUR,
                                                            maxSalaryEUR:
                                                                targetSalary.maxSalaryEUR,
                                                        },
                                                        targetSalary.inputCurrency,
                                                    )}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}
                            </div>

                            <div className="rounded-xl border border-border/70 bg-secondary/30 p-3">
                                <p className="text-[11px] font-semibold text-foreground">
                                    generatedJobTitles
                                </p>
                                {currentJobSearchParams.generatedJobTitles
                                    .length === 0 ? (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        No generated job titles stored yet.
                                    </p>
                                ) : (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {currentJobSearchParams.generatedJobTitles.map(
                                            (jobTitle, index) => (
                                                <Badge
                                                    key={`${jobTitle}-${index}`}
                                                    color={BadgeColor.Neutral}
                                                >
                                                    {jobTitle}
                                                </Badge>
                                            ),
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="rounded-xl border border-border/70 bg-secondary/30 p-3">
                                <p className="text-[11px] font-semibold text-foreground">
                                    sourceLocationMentions
                                </p>
                                {currentJobSearchParams.sourceLocationMentions
                                    .length === 0 ? (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        No source location mentions yet.
                                    </p>
                                ) : (
                                    <ul className="mt-2 space-y-1 text-xs text-foreground">
                                        {currentJobSearchParams.sourceLocationMentions.map(
                                            (locationMention) => (
                                                <li
                                                    key={locationMention.id}
                                                    className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-white/80 p-2"
                                                >
                                                    <span>
                                                        {formatLocationLabel(
                                                            locationMention.city,
                                                            locationMention.country,
                                                        )}
                                                    </span>
                                                    <span className="text-[10px] text-muted-foreground/70">
                                                        {locationMention.id}
                                                    </span>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6 border-t border-border pt-4">
                    <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-foreground">
                            Jobs Offered
                        </h3>
                        <span className="text-[11px] text-muted-foreground">
                            {jobsOffered.length}{' '}
                            {jobsOffered.length === 1 ? 'job' : 'jobs'}
                        </span>
                    </div>

                    {!activeConvoId ? (
                        <p className="mt-3 text-xs text-muted-foreground">
                            Start a Kit conversation to see offered jobs.
                        </p>
                    ) : jobsOffered.length === 0 ? (
                        <p className="mt-3 text-xs text-muted-foreground">
                            No offered jobs yet.
                        </p>
                    ) : (
                        <div className="mt-3 max-h-72 overflow-auto rounded-lg border border-border/60 bg-white/80 p-2">
                            <table className="min-w-full text-xs">
                                <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                    <tr className="border-b border-border/70">
                                        <th className="pb-2 pr-2 text-left font-semibold">
                                            Title
                                        </th>
                                        <th className="pb-2 pr-2 text-left font-semibold">
                                            Company
                                        </th>
                                        <th className="pb-2 pr-2 text-left font-semibold">
                                            Location
                                        </th>
                                        <th className="pb-2 pr-2 text-left font-semibold">
                                            CoreSignal
                                        </th>
                                        <th className="pb-2 pr-2 text-left font-semibold">
                                            Status
                                        </th>
                                        <th className="pb-2 text-left font-semibold">
                                            Reason
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {jobsOffered.map((offeredJob) => (
                                        <tr
                                            key={offeredJob.coreSignalJobId}
                                            className="border-b border-border/60"
                                        >
                                            <td className="py-2 pr-2 align-top">
                                                {offeredJob.title ?? '-'}
                                            </td>
                                            <td className="py-2 pr-2 align-top">
                                                {offeredJob.companyName ?? '-'}
                                            </td>
                                            <td className="py-2 pr-2 align-top">
                                                {offeredJob.location ?? '-'}
                                            </td>
                                            <td className="py-2 pr-2 align-top">
                                                <div className="text-[10px] break-all text-muted-foreground">
                                                    {offeredJob.coreSignalId}
                                                </div>
                                            </td>
                                            <td className="py-2 pr-2 align-top">
                                                <Badge
                                                    color={getOfferedStatusBadgeColor(
                                                        offeredJob.status,
                                                    )}
                                                >
                                                    {offeredJob.status}
                                                </Badge>
                                            </td>
                                            <td className="py-2 align-top">
                                                <span className="text-[11px] text-muted-foreground">
                                                    {offeredJob.reason ?? '-'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="mt-6 border-t border-border pt-4">
                    <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-foreground">
                            Job Search Executions
                        </h3>
                        <span className="text-[11px] text-muted-foreground">
                            {jobSearchExecutions.length}{' '}
                            {jobSearchExecutions.length === 1
                                ? 'execution'
                                : 'executions'}
                        </span>
                    </div>

                    {!activeConvoId ? (
                        <p className="mt-3 text-xs text-muted-foreground">
                            Start a Kit conversation to see job search
                            executions.
                        </p>
                    ) : jobSearchExecutions.length === 0 ? (
                        <p className="mt-3 text-xs text-muted-foreground">
                            No job search executions yet.
                        </p>
                    ) : (
                        <div className="mt-3 space-y-3">
                            {jobSearchExecutions.map((execution) => (
                                <div
                                    key={execution.id}
                                    className="rounded-xl border border-border/70 bg-secondary/30 p-3"
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div>
                                            <p className="text-xs font-semibold text-foreground break-all">
                                                Execution: {execution.id}
                                            </p>
                                            <p className="mt-1 text-[11px] text-muted-foreground">
                                                Created:{' '}
                                                {formatTimestamp(
                                                    execution.createdAt,
                                                )}
                                            </p>
                                            <p className="text-[11px] text-muted-foreground">
                                                Tool call:{' '}
                                                {execution.toolCallId ?? '-'}
                                            </p>
                                        </div>
                                        <Badge color={BadgeColor.Neutral}>
                                            {execution.results.length} results
                                        </Badge>
                                    </div>

                                    <details className="mt-2 text-xs text-muted-foreground">
                                        <summary className="cursor-pointer text-primary">
                                            Filters payload
                                        </summary>
                                        <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-white/80 p-2 text-[10px] leading-relaxed text-foreground">
                                            {formatPayload(
                                                execution.coreSignalFiltersJson,
                                            )}
                                        </pre>
                                    </details>

                                    {execution.results.length === 0 ? (
                                        <p className="mt-2 text-xs text-muted-foreground">
                                            No results recorded.
                                        </p>
                                    ) : (
                                        <div className="mt-2 max-h-72 overflow-auto rounded-lg border border-border/60 bg-white/80 p-2">
                                            <table className="min-w-full text-xs">
                                                <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                                    <tr className="border-b border-border/70">
                                                        <th className="pb-2 pr-2 text-left font-semibold">
                                                            Rank
                                                        </th>
                                                        <th className="pb-2 pr-2 text-left font-semibold">
                                                            Title
                                                        </th>
                                                        <th className="pb-2 pr-2 text-left font-semibold">
                                                            Company
                                                        </th>
                                                        <th className="pb-2 pr-2 text-left font-semibold">
                                                            Location
                                                        </th>
                                                        <th className="pb-2 text-left font-semibold">
                                                            CoreSignal
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {execution.results.map(
                                                        (result) => (
                                                            <tr
                                                                key={result.id}
                                                                className={`border-b border-border/60 ${
                                                                    result.returnedToUser
                                                                        ? 'bg-emerald-50/70'
                                                                        : ''
                                                                }`}
                                                            >
                                                                <td className="py-2 pr-2 align-top">
                                                                    <div className="flex items-center gap-2">
                                                                        <span>
                                                                            {result.rankScore ??
                                                                                '-'}
                                                                        </span>
                                                                        {result.returnedToUser ? (
                                                                            <Badge
                                                                                color={
                                                                                    BadgeColor.Success
                                                                                }
                                                                            >
                                                                                Returned
                                                                            </Badge>
                                                                        ) : null}
                                                                    </div>
                                                                </td>
                                                                <td className="py-2 pr-2 align-top">
                                                                    {result.job
                                                                        ?.title ??
                                                                        '-'}
                                                                </td>
                                                                <td className="py-2 pr-2 align-top">
                                                                    {result.job
                                                                        ?.companyName ??
                                                                        '-'}
                                                                </td>
                                                                <td className="py-2 pr-2 align-top">
                                                                    {result.job
                                                                        ?.location ??
                                                                        '-'}
                                                                </td>
                                                                <td className="py-2 align-top">
                                                                    <div className="text-[10px] break-all text-muted-foreground">
                                                                        {result
                                                                            .job
                                                                            ?.coreSignalId ??
                                                                            '-'}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ),
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <KitConversationTimeline
                    activeConvoId={activeConvoId}
                    transcriptRawData={transcriptRawData}
                    transcriptTurns={transcriptTurns}
                />
            </div>
        </div>
    )
}
